import { zodResolver } from "@hookform/resolvers/zod"
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material"
import { CloseOutlined, WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFieldArray, useForm } from "react-hook-form"

import { AddContactLocationRolesFormFields } from "./AddContactLocationRolesFormFields"
import { EditContactLocationRolesFormFields } from "./EditContactLocationRolesFormFields"

import { Button } from "@/components/common/Button"
import { BLANK_CONTACT_LOCATION_ROLES } from "@/constants/contacts"
import type { ContactLocation } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import type { ContactLocationsEditFormValues } from "@/schemas/contactFormSchema"
import { ContactLocationsEditFormSchema } from "@/schemas/contactFormSchema"
import { useCreateNewContactModal } from "@/screens/Companies/hooks/useCreateNewContact"
import {
  convertContactLocationsToEditFormValues,
  serializeContactLocationAddFormValues,
  serializeContactLocationEditFormValues,
} from "@/screens/Contacts/utils/contactLocations"
import type { DialogCloseAction } from "@/types/common"

export interface EditContactLocationRolesDialogProps {
  open: boolean
  contactId: string
  contactName: string
  contactLocations: ContactLocation[]
  companyId: string
  companyName?: string
  onClose: (action: DialogCloseAction) => void
  onSubmit: () => void
}

const EditContactLocationRolesDialog: FC<EditContactLocationRolesDialogProps> = ({
  open,
  contactId,
  contactName,
  contactLocations = [],
  companyId,
  companyName,
  onClose,
  onSubmit,
}) => {
  const { createContactLocation, deleteContactLocation } = useCreateNewContactModal()
  const { locations } = useGetLocations(companyId)
  const methods = useForm<ContactLocationsEditFormValues>({
    defaultValues: {
      currentLocationRoles: convertContactLocationsToEditFormValues(contactLocations),
      newLocationRoles: [BLANK_CONTACT_LOCATION_ROLES],
    },
    resolver: zodResolver(ContactLocationsEditFormSchema),
    mode: "all",
  })
  const { control, formState, reset, setValue, getValues, watch, handleSubmit } = methods
  const { fields: currentLocationRolesFields, remove } = useFieldArray({ control, name: "currentLocationRoles" })
  const { fields: newLocationRolesFields, append } = useFieldArray({ control, name: "newLocationRoles" })
  const currentLocationRoles = watch("currentLocationRoles")
  const newLocationRoles = watch("newLocationRoles")
  const canSubmit = formState.isValid && formState.isDirty

  const handleLocationAdd = () => append(BLANK_CONTACT_LOCATION_ROLES)

  const handleLocationRemove = (index: number) => remove(index)

  const handleRoleRemove = (index: number, role: string) => {
    const fieldName: `currentLocationRoles.${number}.currentRoles` = `currentLocationRoles.${index}.currentRoles`
    setValue(
      fieldName,
      getValues(fieldName).filter((value) => value !== role),
      { shouldDirty: true }
    )
  }

  const submitLocationRoles = async (formValues: ContactLocationsEditFormValues) => {
    const formContactLocations = [
      ...serializeContactLocationEditFormValues(contactId, formValues.currentLocationRoles),
      ...serializeContactLocationAddFormValues(contactId, formValues.newLocationRoles),
    ]
    const contactLocationsMap = new Map(
      contactLocations.map((item) => [`${item.locationId}-${item.contactRole}`, item])
    )
    const formContactLocationsMap = new Map(
      formContactLocations.map((item) => [`${item.locationId}-${item.contactRole}`, item])
    )

    for (const [key, { contactLocationId }] of contactLocationsMap) {
      if (!formContactLocationsMap.has(key)) {
        await deleteContactLocation({ input: { contactLocationId } })
      }
    }

    for (const [key, contactLocation] of formContactLocationsMap) {
      if (!contactLocationsMap.has(key)) {
        await createContactLocation({ input: contactLocation })
      }
    }

    onSubmit()
    onClose("success")
    reset()
  }

  const handleCancel = () => {
    reset()
    onClose("cancel")
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        onClose(reason)
        reset()
      }}
      fullWidth
    >
      <form onSubmit={handleSubmit(submitLocationRoles)}>
        <DialogTitle className='pb-3 pl-10 font-normal text-primary'>
          {contactName}
          {companyName && (
            <Typography variant='subtitle1' className='text-gray-700'>
              {companyName}
            </Typography>
          )}
        </DialogTitle>
        <IconButton
          aria-label='close'
          onClick={() => {
            onClose("cancel")
            reset()
          }}
          className='absolute right-4 top-3 text-primary'
        >
          <CloseOutlined />
        </IconButton>
        <Divider />
        <DialogContent classes={{ root: "p-10" }}>
          {contactLocations.length > 0 && (
            <>
              <Typography variant='body1' fontWeight={700} className='mb-4 leading-6 tracking-[0.15px] text-gray-700'>
                Current Locations' Roles
              </Typography>
              {currentLocationRoles.length > 0 ? (
                <EditContactLocationRolesFormFields
                  fieldArrayName={"currentLocationRoles"}
                  control={control}
                  fields={currentLocationRolesFields}
                  locations={locations}
                  locationRoles={currentLocationRoles}
                  onLocationRemove={handleLocationRemove}
                  onRoleRemove={handleRoleRemove}
                />
              ) : (
                <Alert
                  className='bg-tertiary-yellow-100 text-tertiary-yellow-900'
                  severity='warning'
                  icon={<WarningOutlined className='text-tertiary-yellow-700' />}
                >
                  Current locations' roles' have been removed
                </Alert>
              )}
              <Divider className='mb-8 mt-4' />
            </>
          )}
          <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
            {contactLocations.length === 0 ? "Locations' Roles" : "Additional Locations’ Roles"}
          </Typography>
          <AddContactLocationRolesFormFields<ContactLocationsEditFormValues>
            fieldArrayName={"newLocationRoles"}
            contactId={contactId}
            contactName={contactName}
            methods={methods}
            control={control}
            fields={newLocationRolesFields}
            locations={locations}
            currentLocationRoles={currentLocationRoles}
            newLocationRoles={newLocationRoles}
            onAdd={handleLocationAdd}
          />
        </DialogContent>
        <DialogActions className='justify-between px-10 pb-10'>
          <Button appearance='outlined' onClick={handleCancel}>
            Cancel
          </Button>
          <Button type='submit' disabled={!canSubmit}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default EditContactLocationRolesDialog
